import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import withTranslation from 'hoc/withTranslation';
import {
  RETURN_LOCATION_MODE,
  selectTourById,
  setDisplayParam,
  setOrders,
  setSolutionJson,
  setTourParameter,
  setUserParam,
  updateOrder,
  updatePudo,
  updateReload,
  removeReload,
  updateParking,
  removeParking,
} from 'actions';
import { v4 } from 'uuid';
import { hasOngoingAsyncRequest } from 'utils/SolutionHelpers';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import {
  isActivityToDisplay,
  isPointDepot,
  isPointReturnLocation,
  isUnassignedJob,
} from '../../../utils/map/MapFeatureFilters';
import { makeMarker } from '../../../utils/map/MapMarkersUtils';
import { coordMatchByCoords, updateBreakLocation } from '../../../utils/map/MarkerDragHandler';
import { createClusterCustomIcon } from '../../../utils/map/MapClusterUtils';
import { AMPLITUDE_EVENTS, AmplitudeService } from '../../../utils/amplitude';
import { getLocationAddress } from '../../../utils/GeoCoder';
import { getSafeValue } from '../../../utils/security';
import ActivityMarkers from './ActivityMarkers';
import StartEndLocations from './StartEndLocations';
import StopMarkers from './StopMarkers';
import JobMarkers from './JobMarkers';
import UnselectedMarkers from './UnselectedMarkers';
import UndesiredMarkers from './UndesiredMarkers';

export const DEFAULT_CLUSTER_RADIUS = 50;
export const MIN_CLUSTER_RADIUS = 40;
export const CLUSTER_BREAKPOINT_MAP_ZOOM = 12;

export const ACTIVITY_DEFAULT_CLUSTER_RADIUS = 100;
export const ACTIVITY_MIN_CLUSTER_RADIUS = 20;

const MapMarkers = ({
  mapData,
  map,
  user,
  display,
  routingRoutes,
  tourData,
  routingData,
  isSolution,
  activateTour,
  showUnassigned,
  translations,
  oAuth,
  tourPlanner,
  solution,
  orders,
  helpers: { formatDuration },
}) => {
  const dispatch = useDispatch();
  const removeHighlight =
    useSelector(({ usageContext: stateUsageContext }) => stateUsageContext).currentStep === 4;
  const reloads = useSelector((s) => s.reloads);
  const parkings = useSelector((s) => s.parkings);
  const showIntersections = user.mapSettings.showIntersections;

  const hourOffset = tourPlanner.offset;
  const displayUnselected = isSolution && tourData;
  const tempOrders = solution.requests[solution.show].tempOrders;

  const groupedDistances = mapData.geo.features.reduce((acc, feature) => {
    const distanceData = feature.properties.distance;
    if (distanceData) {
      const { tourIndex, distance } = distanceData;
      if (tourIndex !== undefined && distance > 0) {
        if (!acc[tourIndex]) {
          acc[tourIndex] = [];
        }
        acc[tourIndex].push(distance);
      }
    }
    return acc;
  }, {});

  const [highlightStop, setHighlightStop] = useState({ id: -1, tourId: 0 });
  const [highlightUnassigned, setHighlightUnassigned] = useState(false);
  const [highlightUnassignedId, setHighlightUnassignedId] = useState(false);

  const handleSetTourParameter = useCallback(
    (parameter) => dispatch(setTourParameter({ ...parameter, index: solution.show })),
    [dispatch, solution.show],
  );
  const handleSelectTourById = useCallback((data) => dispatch(selectTourById(data)), [dispatch]);
  const handleSetDisplayParameter = useCallback(
    (data) => dispatch(setDisplayParam(data)),
    [dispatch],
  );

  const handleSetSolutionJson = useCallback(
    (param) => dispatch(setSolutionJson(param)),
    [dispatch],
  );
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);

  const handleSetOrders = useCallback(
    (newOrders) => dispatch(setOrders(newOrders, false, solution.show)),
    [dispatch, solution.show],
  );

  const handleUpdateOrder = useCallback(
    (o, idx, ordIdx) => dispatch(updateOrder(o, idx, ordIdx)),
    [dispatch],
  );

  const onMarkerClick = useCallback(
    (feature, isPlan) => {
      const isFleetStop = feature.properties.isFleetStop;
      if (isFleetStop) return;

      const isUnassigned = isUnassignedJob(feature);
      const tourId = feature.properties.routeId;
      const id = isActivityToDisplay(feature)
        ? feature.properties.stopIndex
        : feature.properties.jobOrder;
      const activeTour = tourData?.tours ? getSafeValue(tourData?.tours, tourId) : undefined;

      if (highlightStop?.id === id && highlightStop?.tourId === tourId) {
        setHighlightStop({ id: -1, tourId });
        handleSetDisplayParameter({ highlightStop: null });
      } else {
        activateTour(tourId, activeTour);
        setHighlightStop({ id, tourId });
        handleSetDisplayParameter({ highlightStop: id });
      }

      if (isPlan) {
        const jobId = feature.properties.id;
        const indexFound = orders.findIndex((order) => order.InternalID === jobId);
        const currValue = orders[indexFound]?.Highlight;
        orders[indexFound] = {
          ...orders[indexFound],
          Highlight: !currValue ? true : !currValue,
        };
        dispatch(setOrders(orders));
        handleSetSolutionJson({ jsonTriggerVRP: true });
      }

      if (isUnassigned) {
        showUnassigned();
        setHighlightUnassigned((prev) => isUnassigned && !prev);
      }
      if (tourId === undefined) return;

      if (display.selectedTour !== tourId && !id) {
        const tour = getSafeValue(tourData.tours, tourId);
        activateTour(tourId, tour);
        AmplitudeService.log(AMPLITUDE_EVENTS.MAP_MARKER_ACTIVATE_TOUR);
        setHighlightStop({ id: -1, tourId });
      }
    },
    [
      tourData,
      activateTour,
      showUnassigned,
      orders,
      dispatch,
      display,
      highlightStop,
      handleSetDisplayParameter,
      handleSetSolutionJson,
    ],
  );

  const onMarkerDragEnd = useCallback(
    (m, feature) => {
      const latlng = m.getLatLng();
      const oldCoords = [...feature.geometry.coordinates];
      feature.geometry.coordinates = [latlng.lng, latlng.lat];

      getLocationAddress(latlng, oAuth, user).then((item) => {
        const loc = {
          label: item.address.label,
          value: item.position,
        };
        const isEditing = feature.properties.isEditing;
        const featureID = feature.properties.id;
        const isPickup = feature.properties.isPickup;
        handleSelectTourById({ selectedTour: display.selectedTour, noMapMovement: true });

        if (feature.properties.isFleetStop) {
          const types = feature.properties.types || [];
          if (types.includes('reload')) {
            const [oldLng, oldLat] = oldCoords;
            const match = reloads.find(coordMatchByCoords(oldLat, oldLng));
            if (match) {
              dispatch(
                updateReload({
                  id: match.id,
                  lat: loc.value.lat,
                  lng: loc.value.lng,
                  address: loc.label,
                }),
              );
            }
            handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
          } else if (types.includes('parking')) {
            const [oldLng, oldLat] = oldCoords;
            const match = parkings.find(coordMatchByCoords(oldLat, oldLng));
            if (match) {
              dispatch(
                updateParking({
                  id: match.id,
                  lat: loc.value.lat,
                  lng: loc.value.lng,
                  address: loc.label,
                }),
              );
            }
            handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
          } else if (types.includes('break')) {
            const updatedBreaks = updateBreakLocation(
              tourPlanner.breaks || [],
              oldCoords,
              loc.value.lat,
              loc.value.lng,
              loc.label,
            );
            handleSetTourParameter({ breaks: updatedBreaks });
            handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
          }
        } else if (isPointDepot(feature)) {
          const toSet = { location: loc };
          if (tourPlanner.returnLocationMode === RETURN_LOCATION_MODE.START)
            toSet.returnLocation = loc;
          handleSetTourParameter(toSet);
          if (solution.json) {
            handleSetSolutionJson({ jsonUserChange: 'ordersDepot' });
            handleSetSolutionJson({ jsonTriggerVRP: true, jsonLocationOnly: true });
          }
        } else if (isPointReturnLocation(feature)) {
          handleSetTourParameter({ returnLocation: loc });
          if (solution.json) {
            handleSetSolutionJson({ jsonUserChange: 'ordersEndLoc' });
            handleSetSolutionJson({ jsonTriggerVRP: true, jsonLocationOnly: true });
          }
        } else if (feature.properties.isPudo) {
          dispatch(
            updatePudo({
              id: featureID,
              lat: loc.value.lat,
              lng: loc.value.lng,
              address: loc.label,
            }),
          );

          const pudoOrderIndex = orders.findIndex((o) => o.InternalID === featureID);
          if (pudoOrderIndex >= 0) {
            const updatedOrders = [...orders];
            updatedOrders[pudoOrderIndex] = {
              ...updatedOrders[pudoOrderIndex],
              Address: loc.label,
              Latitude: loc.value.lat,
              Longitude: loc.value.lng,
            };
            handleSetOrders(updatedOrders);
          }

          handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
        } else {
          const orderIndex = orders.findIndex(
            (order) =>
              order.InternalID === featureID &&
              order.Activity === (isPickup ? 'pickup' : 'delivery'),
          );

          if (orderIndex >= 0) {
            orders[orderIndex] = {
              ...(isEditing ? tourPlanner.editedOrder : orders[orderIndex]),
              InternalID: !isEditing ? orders[orderIndex].InternalID : v4(),
              Address: loc.label,
              Latitude: loc.value.lat,
              Longitude: loc.value.lng,
              Activity: feature.properties.types[0],
              Dragged: true,
              TimeCreated: Date.now(),
            };
          }

          if (!isEditing) {
            handleSetOrders(orders);
            handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
          } else
            handleSetTourParameter({
              editedOrder: {
                ...tourPlanner.editedOrder,
                Address: loc.label,
                Latitude: loc.value.lat,
                Longitude: loc.value.lng,
              },
            });
        }
      });
    },
    [
      oAuth,
      user,
      tourPlanner,
      reloads,
      parkings,
      dispatch,
      handleSetTourParameter,
      handleSetOrders,
      handleUpdateOrder,
      orders,
      solution.show,
      solution.json,
      handleSetSolutionJson,
      handleSelectTourById,
      display.selectedTour,
    ],
  );

  const onFleetStopDelete = useCallback(
    (feature) => {
      const [lng, lat] = feature.geometry.coordinates;
      const types = feature.properties.types || [];
      const coordMatch = coordMatchByCoords(lat, lng);

      if (types.includes('break')) {
        const updatedBreaks = (tourPlanner.breaks || []).filter((b) => !coordMatch(b.location));
        handleSetTourParameter({ breaks: updatedBreaks.length > 0 ? updatedBreaks : undefined });
        handleSetSolutionJson({ jsonTriggerVRP: true });
      } else if (types.includes('reload')) {
        const match = reloads.find(coordMatchByCoords(lat, lng));
        if (match) dispatch(removeReload(match.id));
        handleSetSolutionJson({ jsonTriggerVRP: true });
      } else if (types.includes('parking')) {
        const match = parkings.find(coordMatchByCoords(lat, lng));
        if (match) dispatch(removeParking(match.id));
        handleSetSolutionJson({ jsonTriggerVRP: true });
      }
    },
    [tourPlanner, reloads, parkings, dispatch, handleSetTourParameter, handleSetSolutionJson],
  );

  const highlightedJobIds = useMemo(() => {
    if (highlightStop.id === -1 || highlightStop.id == null) return null;
    const features = mapData?.geo?.features || [];
    const hlFeature = features.find(
      (f) =>
        f.geometry.type === 'Point' &&
        f.properties.jobOrder === highlightStop.id &&
        f.properties.routeId === highlightStop.tourId &&
        f.properties.jobIds,
    );
    return hlFeature ? new Set(hlFeature.properties.jobIds) : null;
  }, [mapData, highlightStop.id, highlightStop.tourId]);

  const makeMapMarkers = useCallback(
    (feature, latlng) => {
      const isDirectHighlight =
        (display.selectedTour === highlightStop.tourId &&
          (feature.properties.jobOrder === highlightStop.id ||
            feature.properties.stopIndex === highlightStop.id)) ||
        (display.highlightStop != null &&
          feature.properties.jobOrder === display.highlightStop &&
          feature.properties.routeId === display.selectedTour);
      const isConnected =
        highlightedJobIds &&
        feature.properties.jobIds &&
        feature.properties.jobOrder !== highlightStop.id &&
        feature.properties.routeId === highlightStop.tourId &&
        feature.properties.jobIds.some((jid) => highlightedJobIds.has(jid));
      const highlight = isDirectHighlight || !!isConnected;
      return makeMarker(
        feature,
        latlng,
        display,
        onMarkerClick,
        translations,
        onMarkerDragEnd,
        isSolution,
        hourOffset,
        highlight,
        highlightUnassigned,
        hasOngoingAsyncRequest(solution.requests),
        dispatch,
        isEmpty(orders) ? tempOrders : orders,
        solution,
        formatDuration,
        groupedDistances,
        highlightUnassignedId,
        onFleetStopDelete,
        parkings,
      );
    },
    [
      display,
      translations,
      isSolution,
      hourOffset,
      highlightStop,
      highlightedJobIds,
      onMarkerClick,
      onMarkerDragEnd,
      highlightUnassigned,
      highlightUnassignedId,
      solution.requests,
      dispatch,
      orders,
      solution,
      groupedDistances,
      tempOrders,
      onFleetStopDelete,
      parkings,
    ],
  );

  const createClusterIcon = useCallback(
    (cluster) => {
      const effectiveHighlight =
        highlightStop.id !== -1
          ? highlightStop
          : display.highlightStop != null
          ? { id: display.highlightStop, tourId: display.selectedTour }
          : highlightStop;
      return createClusterCustomIcon(cluster, effectiveHighlight, map, translations, highlightedJobIds);
    },
    [highlightStop, highlightedJobIds, translations, map, display.highlightStop, display.selectedTour],
  );

  useEffect(() => {
    if (removeHighlight) setHighlightUnassigned(false);
  }, [removeHighlight]);

  useEffect(() => {
    if (display.highlightStop != null) {
      setHighlightStop((prev) => {
        if (prev.id === display.highlightStop && prev.tourId === display.selectedTour) return prev;
        return { id: display.highlightStop, tourId: display.selectedTour };
      });
    } else {
      setHighlightStop((prev) => {
        if (prev.id === -1) return prev;
        return { id: -1, tourId: prev.tourId };
      });
    }
  }, [display.highlightStop, display.selectedTour]);

  useEffect(() => {
    handleSetUserParam({ highlightStop });
  }, [highlightStop]);

  useEffect(() => {
    const overlapCount = routingRoutes?.features.filter(
      (f) => f?.properties.isUndesired && f.geometry.type === 'Point',
    )[0]?.properties.overlapCount;

    if (user?.overlapCount === undefined && isNumber(overlapCount))
      handleSetUserParam({ overlapCount });
  }, [routingRoutes?.features, user?.overlapCount, handleSetUserParam]);

  useEffect(() => {
    const ords = isEmpty(orders) ? tempOrders : orders;
    const index = ords?.findIndex((o) => isEqual(o, user.zoomUnassigned?.order));
    const order = index >= 0 ? ords[index] : null;
    setHighlightUnassignedId(order?.InternalID || false);
    setHighlightUnassigned(false);
  }, [user.zoomUnassigned]);

  return (
    <>
      <UnselectedMarkers
        makeMapMarkers={makeMapMarkers}
        mapData={mapData}
        user={user}
        display={display}
        routingRoutes={routingRoutes}
        displayUnselected={displayUnselected}
        highlightUnassigned={highlightUnassigned || highlightUnassignedId}
      />
      <StopMarkers
        makeMapMarkers={makeMapMarkers}
        mapData={mapData}
        user={user}
        display={display}
        routingRoutes={routingRoutes}
        highlightStop={highlightStop}
        createClusterIcon={createClusterIcon}
        isSolution={isSolution}
        routingData={routingData}
      />
      {!!tourData && showIntersections && routingRoutes && (
        <UndesiredMarkers
          makeMapMarkers={makeMapMarkers}
          mapData={mapData}
          user={user}
          display={display}
          routingRoutes={routingRoutes}
          highlightStop={highlightStop}
          createClusterIcon={createClusterIcon}
        />
      )}
      {display.showProblem && (
        <JobMarkers
          makeMapMarkers={makeMapMarkers}
          mapData={mapData}
          user={user}
          display={display}
          routingRoutes={routingRoutes}
          highlightStop={highlightStop}
          createClusterIcon={createClusterIcon}
          isSolution={isSolution}
          routingData={routingData}
        />
      )}
      {user.mapSettings.showStops && (
        <>
          <StartEndLocations
            makeMapMarkers={makeMapMarkers}
            mapData={mapData}
            user={user}
            display={display}
            routingRoutes={routingRoutes}
            routingData={routingData}
            isSolution={isSolution}
          />
          <ActivityMarkers
            mapData={mapData}
            user={user}
            display={display}
            routingRoutes={routingRoutes}
            highlightStop={highlightStop}
            makeMapMarkers={makeMapMarkers}
            createClusterIcon={createClusterIcon}
          />
        </>
      )}
    </>
  );
};

export default withTranslation(MapMarkers);
